/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Divider,
  Alert,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { TemplateInfo } from '../../types/agent';
import { formatDistanceToNow } from 'date-fns';

interface TemplateInfoCardProps {
  templateInfo: TemplateInfo;
}

export const TemplateInfoCard: React.FC<TemplateInfoCardProps> = ({
  templateInfo,
}) => {
  if (!templateInfo.is_template_based) {
    return null;
  }

  const { 
    template_version, 
    current_template_version, 
    needs_sync, 
    last_synced_at, 
    is_customized 
  } = templateInfo;

  return (
    <Card variant="outlined">
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <InfoIcon color="primary" />
          <Typography variant="h6">Template Information</Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box display="flex" flexDirection="column" gap={2}>
          {/* Version Info */}
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Version:
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Chip
                label={`v${template_version}`}
                size="small"
                color={needs_sync ? 'warning' : 'success'}
              />
              {current_template_version !== template_version && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    →
                  </Typography>
                  <Chip
                    label={`v${current_template_version} (latest)`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </>
              )}
            </Box>
          </Box>

          {/* Sync Status */}
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Status:
            </Typography>
            {needs_sync ? (
              <Alert severity="warning" icon={<WarningIcon />} sx={{ py: 0.5 }}>
                <Typography variant="body2">
                  Update available - newer template version detected
                </Typography>
              </Alert>
            ) : (
              <Alert severity="success" icon={<CheckCircleIcon />} sx={{ py: 0.5 }}>
                <Typography variant="body2">Up to date</Typography>
              </Alert>
            )}
          </Box>

          {/* Last Synced */}
          {last_synced_at && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Last Synced:
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <ScheduleIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  {formatDistanceToNow(new Date(last_synced_at), {
                    addSuffix: true,
                  })}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Customization Status */}
          {is_customized && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              <Typography variant="body2">
                <strong>Customized:</strong> Your changes will be preserved during sync
              </Typography>
            </Alert>
          )}

          {/* Help Text */}
          <Box>
            <Typography variant="caption" color="text.secondary">
              This agent is managed from a template. Updates from admins will be
              automatically synced while preserving your customizations.
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
